import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { analytics } from '../services/analytics';
import SEOPageLayout from '../components/SEOPageLayout';

const ArrowUpIcon = (props) => (
  <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 19V5M5 12l7-7 7 7"/>
  </svg>
);

const CaseStudies = () => {
  useEffect(() => {
    analytics.track('Case Studies Page View');
  }, []);

  const caseStudies = [
    {
      industry: 'HVAC Contractor',
      company: 'Comfort Air Solutions',
      location: 'Phoenix, AZ',
      challenge: 'Losing 40% of quotes to competitors offering lower prices. Customers always chose the cheapest option.',
      solution: 'Implemented Good/Better/Best pricing tiers with clear value differentiation for each package level.',
      results: [
        { metric: 'Average Deal Size', value: '+38%', description: 'Customers consistently chose Better and Best packages' },
        { metric: 'Close Rate', value: '+24%', description: 'Interactive quotes reduced back-and-forth by 60%' },
        { metric: 'Quote Time', value: '-75%', description: 'From 30 minutes to under 8 minutes per quote' }
      ],
      quote: 'Before QuoteTier, customers always asked for the cheapest option. Now they see the value in our premium packages and choose them without me having to push.',
      author: 'Mike R., Owner'
    },
    {
      industry: 'Web Design Agency',
      company: 'Pixel Perfect Studios',
      location: 'Austin, TX',
      challenge: 'Spending hours creating custom proposals for each client. Many prospects ghosted after receiving quotes.',
      solution: 'Created standardized package templates with add-on options clients could select themselves.',
      results: [
        { metric: 'Revenue per Client', value: '+52%', description: 'Clients added premium features during quote review' },
        { metric: 'Proposal Time', value: '-80%', description: 'Templates reduced quote creation to 5 minutes' },
        { metric: 'Ghost Rate', value: '-45%', description: 'E-sign tracking showed when clients viewed quotes' }
      ],
      quote: 'The interactive format changed everything. Clients explore our packages like they\'re shopping online, and they always add more than I would have suggested.',
      author: 'Sarah T., Founder'
    },
    {
      industry: 'Painting Contractor',
      company: 'Premier Paint Pros',
      location: 'Denver, CO',
      challenge: 'Competitors were undercutting prices. Premium paint quality was hard to explain over the phone.',
      solution: 'Used interactive quotes to visually showcase the difference between paint quality tiers.',
      results: [
        { metric: 'Average Job Value', value: '+41%', description: 'Customers upgraded to premium paint options' },
        { metric: 'Win Rate', value: '+33%', description: 'Professional quotes stood out from competitors' },
        { metric: 'Time to Signature', value: '-60%', description: 'Built-in e-signatures eliminated paperwork delays' }
      ],
      quote: 'Customers used to just see a number. Now they see exactly what they\'re getting with each tier. They understand why our premium option costs more - and they choose it.',
      author: 'James P., Operations Manager'
    },
    {
      industry: 'Wedding Photography',
      company: 'Moments Captured',
      location: 'Nashville, TN',
      challenge: 'Brides wanted to customize packages but email chains got confusing. Lost sales due to slow response times.',
      solution: 'Created interactive packages where couples could build their perfect photography experience.',
      results: [
        { metric: 'Average Booking', value: '+48%', description: 'Couples added albums, engagement sessions, and extra hours' },
        { metric: 'Booking Speed', value: '+65%', description: 'Instant e-signature reduced decision time' },
        { metric: 'Inquiries Converted', value: '+28%', description: 'Professional quotes impressed potential clients' }
      ],
      quote: 'Couples love being able to explore options and see exactly how each add-on affects their package. It\'s like shopping for their wedding photography online.',
      author: 'Emily K., Lead Photographer'
    },
    {
      industry: 'Landscaping Services',
      company: 'Green Valley Landscapes',
      location: 'Seattle, WA',
      challenge: 'Complex projects were hard to quote. Customers didn\'t understand what was included in each tier.',
      solution: 'Built visual package comparisons showing exactly what\'s included in Basic, Premium, and Luxury tiers.',
      results: [
        { metric: 'Project Value', value: '+45%', description: 'Clients chose higher tiers when they saw the value' },
        { metric: 'Quote Acceptance', value: '+37%', description: 'Clear pricing reduced negotiation and objections' },
        { metric: 'Admin Time', value: '-70%', description: 'Automated quotes freed up office staff' }
      ],
      quote: 'We went from spending hours explaining quotes over the phone to letting clients explore packages themselves. They always come back wanting more.',
      author: 'David M., CEO'
    }
  ];

  return (
    <SEOPageLayout
      title="Case Studies | How Businesses Increased Deal Size with QuoteTier"
      description="See how HVAC contractors, web designers, photographers, and other businesses increased their average deal size by 38-52% using QuoteTier's interactive quotes."
      canonical="/case-studies"
      ogTitle="QuoteTier Case Studies - Real Results from Real Businesses"
      ogDescription="Learn how service businesses increased their average deal size using interactive Good/Better/Best pricing. Read success stories from contractors, agencies, and freelancers."
    >
      {/* Hero Section */}
      <section className="py-24 md:py-32">
        <div className="container max-w-7xl mx-auto px-4">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="font-instrument text-4xl sm:text-5xl md:text-6xl lg:text-7xl tracking-tight leading-tight mb-6">
              Real <span className="text-primary font-bold">results</span> from real businesses
            </h1>
            <p className="font-jetbrains text-xl md:text-2xl text-gray-600 tracking-tight leading-relaxed">
              See how service businesses are using interactive quotes to increase their average deal size.
            </p>
          </div>
        </div>
      </section>

      {/* Stats Overview */}
      <section className="py-16 bg-black text-white">
        <div className="container max-w-5xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            <div>
              <div className="text-5xl md:text-6xl font-bold text-green-400 mb-2">+42%</div>
              <p className="font-jetbrains text-gray-300">Average increase in deal size</p>
            </div>
            <div>
              <div className="text-5xl md:text-6xl font-bold text-green-400 mb-2">+31%</div>
              <p className="font-jetbrains text-gray-300">Average improvement in close rate</p>
            </div>
            <div>
              <div className="text-5xl md:text-6xl font-bold text-green-400 mb-2">-70%</div>
              <p className="font-jetbrains text-gray-300">Reduction in quote creation time</p>
            </div>
          </div>
        </div>
      </section>

      {/* Case Studies */}
      <section className="py-16 md:py-24">
        <div className="container max-w-5xl mx-auto px-4">
          <div className="space-y-16">
            {caseStudies.map((study, index) => (
              <Card key={index} className="border border-gray-200 overflow-hidden">
                <CardHeader className="bg-gray-50 border-b border-gray-200">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                    <div>
                      <p className="font-jetbrains text-sm text-gray-500 uppercase tracking-wider">{study.industry}</p>
                      <CardTitle className="font-instrument text-2xl">{study.company}</CardTitle>
                    </div>
                    <p className="font-jetbrains text-sm text-gray-500">{study.location}</p>
                  </div>
                </CardHeader>
                <CardContent className="p-6 md:p-8">
                  <div className="space-y-6">
                    <div>
                      <h3 className="font-instrument text-lg font-bold mb-2">The Challenge</h3>
                      <p className="font-jetbrains text-gray-600">{study.challenge}</p>
                    </div>

                    <div>
                      <h3 className="font-instrument text-lg font-bold mb-2">The Solution</h3>
                      <p className="font-jetbrains text-gray-600">{study.solution}</p>
                    </div>

                    <div>
                      <h3 className="font-instrument text-lg font-bold mb-4">The Results</h3>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {study.results.map((result, i) => (
                          <div key={i} className="bg-green-50 border border-green-100 rounded-xl p-4 text-center">
                            <div className="flex items-center justify-center gap-1 text-green-600 mb-1">
                              <ArrowUpIcon className="h-5 w-5" />
                              <span className="text-2xl font-bold">{result.value}</span>
                            </div>
                            <p className="font-instrument font-bold text-sm">{result.metric}</p>
                            <p className="font-jetbrains text-xs text-gray-600 mt-1">{result.description}</p>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="bg-gray-50 rounded-xl p-6 border-l-4 border-black">
                      <p className="font-jetbrains text-gray-700 italic mb-3">"{study.quote}"</p>
                      <p className="font-jetbrains text-sm text-gray-500">— {study.author}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-gray-50">
        <div className="container max-w-3xl mx-auto px-4 text-center">
          <h2 className="font-instrument text-4xl md:text-5xl font-bold tracking-tight mb-6">
            Ready to write your success story?
          </h2>
          <p className="font-jetbrains text-xl text-gray-600 mb-10">
            Join these businesses and start increasing your average deal size today.
          </p>
          <Link
            to="/signup"
            onClick={() => analytics.track('CTA Clicked', { location: 'case-studies', action: 'signup' })}
          >
            <Button className="px-10 py-4 font-instrument text-xl font-semibold bg-black text-white hover:bg-gray-800 rounded-lg shadow-lg">
              Start Free Today
            </Button>
          </Link>
          <p className="font-jetbrains text-sm text-gray-500 mt-4">
            No credit card required • 3 free quotes per month
          </p>
        </div>
      </section>
    </SEOPageLayout>
  );
};

export default CaseStudies;
